import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const CurrencyContext = createContext(null);

const SYMBOLS = { CAD: "$", USD: "$", EUR: "€", GBP: "£", AUD: "$" };
const ORDER = ["CAD", "USD", "EUR", "GBP", "AUD"];

// Map a browser locale region to one of our supported currencies (else CAD).
const detectCurrency = () => {
  try {
    const region = (navigator.language || "").split("-")[1]?.toUpperCase();
    const EU = ["FR", "DE", "ES", "IT", "NL", "BE", "IE", "PT", "AT", "FI", "GR", "LU"];
    if (region === "US") return "USD";
    if (region === "GB" || region === "UK") return "GBP";
    if (region === "AU") return "AUD";
    if (EU.includes(region)) return "EUR";
    return "CAD";
  } catch {
    return "CAD";
  }
};

export const CurrencyProvider = ({ children }) => {
  const [rates, setRates] = useState({ CAD: 1 });
  const [supported, setSupported] = useState([{ code: "CAD", symbol: "$", label: "Canadian Dollar", rate: 1 }]);
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("lira_currency") || null);

  useEffect(() => {
    api.get("/currencies")
      .then((r) => {
        setRates(r.data.rates || { CAD: 1 });
        setSupported(r.data.supported || []);
        if (!localStorage.getItem("lira_currency")) {
          setCurrencyState(detectCurrency());
        }
      })
      .catch(() => {});
  }, []);

  const currentCurrency = currency || "CAD";

  const setCurrency = (c) => {
    const code = ORDER.includes(c) ? c : "CAD";
    setCurrencyState(code);
    localStorage.setItem("lira_currency", code);
  };

  const rate = rates[currentCurrency] || 1;
  const symbol = SYMBOLS[currentCurrency] || "$";

  // Convert a CAD amount and format with symbol + code.
  const format = (cad) => `${symbol}${(Number(cad || 0) * rate).toFixed(2)} ${currentCurrency}`;
  const convert = (cad) => Number((Number(cad || 0) * rate).toFixed(2));

  return (
    <CurrencyContext.Provider value={{ currency: currentCurrency, setCurrency, rate, symbol, rates, supported, format, convert }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
